import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "../../hooks";
import { DIALOG_ACTIONS_SX, DIALOG_PAPER_SX, DIALOG_TITLE_SX } from "./ui/dialogStyles";
import { PaymentService } from "../../services/PaymentService";
import { generateIdempotencyKey } from "../../utils/idempotencyKey";
import { extractApiError } from "../../utils/extractApiError";
import { formatMoney } from "./appointmentPresentation";
export function PortalPaymentClaimSheet({ open, appointment, methods, onClose, onClaimed, }) {
    const { t } = useTranslation("portal");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [methodId, setMethodId] = React.useState("");
    const [reference, setReference] = React.useState("");
    const [file, setFile] = React.useState(null);
    const [fileError, setFileError] = React.useState(null);
    const keyRef = React.useRef(generateIdempotencyKey());
    const mutation = useMutation({
        mutationFn: async (vars) => {
            const proofUrl = vars.file
                ? await PaymentService.uploadPaymentProof(vars.appointmentId, vars.file)
                : null;
            return PaymentService.submitPaymentClaim(vars.appointmentId, {
                paymentMethodId: vars.paymentMethodId,
                paymentReference: vars.paymentReference,
                amount: vars.amount,
                currency: vars.currency,
                proofUrl,
            }, keyRef.current);
        },
        onSuccess: onClaimed,
    });
    React.useEffect(() => {
        if (!open)
            return;
        keyRef.current = generateIdempotencyKey();
        mutation.reset();
        setMethodId(methods[0]?.id ?? "");
        setReference("");
        setFile(null);
        setFileError(null);
    }, [open, appointment?.id, methods]);
    if (!appointment)
        return null;
    const amount = appointment.paymentAmountDue ?? appointment.consultationCharge ?? null;
    const hasEvidence = reference.trim().length > 0 || file !== null;
    const handleFile = (selected) => {
        if (!selected) {
            setFile(null);
            setFileError(null);
            return;
        }
        const rejection = PaymentService.validateProofFile(selected);
        setFileError(rejection);
        setFile(rejection ? null : selected);
    };
    return (_jsxs(Dialog, { open: open, onClose: mutation.isPending ? undefined : onClose, fullWidth: true, maxWidth: "xs", fullScreen: fullScreen, slotProps: { paper: { sx: DIALOG_PAPER_SX } }, children: [_jsx(DialogTitle, { sx: DIALOG_TITLE_SX, children: t("claimButton", "I've completed the payment") }), _jsxs(DialogContent, { children: [amount != null ? (_jsxs(Box, { sx: (muiTheme) => ({
                            mb: 2,
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${muiTheme.palette.divider}`,
                            backgroundColor: alpha(muiTheme.palette.primary.main, 0.04),
                        }), children: [_jsx(Typography, { sx: {
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "text.secondary",
                                }, children: t("payment", "Payment") }), _jsx(Typography, { sx: { fontWeight: 700, fontSize: 20, mt: 0.25 }, children: formatMoney(amount, appointment.currency) })] })) : null, _jsx(Alert, { severity: "warning", sx: { mb: 2, borderRadius: 2 }, children: t("claimPending", "Payment pending verification — add your transaction reference or a screenshot so the clinic can find your payment.") }), _jsxs(Stack, { spacing: 2, children: [methods.length > 0 ? (_jsx(TextField, { select: true, fullWidth: true, size: "small", label: t("payment", "Payment"), value: methodId === "" ? "" : String(methodId), onChange: (event) => setMethodId(Number(event.target.value)), disabled: mutation.isPending, children: methods.map((method) => (_jsx(MenuItem, { value: String(method.id), children: method.name }, method.id))) })) : null, _jsx(TextField, { fullWidth: true, size: "small", label: t("paymentReference", "Payment reference"), placeholder: t("paymentReferencePlaceholder", "UTR / transaction ID"), value: reference, onChange: (event) => setReference(event.target.value), disabled: mutation.isPending, helperText: t("paymentReferenceHelp", "You can find this in your banking or payment app.") }), _jsxs(Box, { children: [_jsxs(Button, { component: "label", variant: "outlined", fullWidth: true, startIcon: _jsx(AttachFileRoundedIcon, { sx: { fontSize: 17 } }), disabled: mutation.isPending, sx: (muiTheme) => ({
                                            borderRadius: 2,
                                            py: 1.25,
                                            borderStyle: "dashed",
                                            color: "text.secondary",
                                            "&:hover": {
                                                borderStyle: "dashed",
                                                borderColor: muiTheme.palette.primary.main,
                                                backgroundColor: alpha(muiTheme.palette.primary.main, 0.04),
                                            },
                                        }), children: [t("attachScreenshot", "Attach screenshot"), _jsx("input", { type: "file", hidden: true, accept: "image/png,image/jpeg,image/webp", onChange: (event) => handleFile(event.target.files?.[0] ?? null) })] }), file ? (_jsx(Typography, { variant: "caption", sx: {
                                            display: "block",
                                            mt: 0.75,
                                            color: "text.secondary",
                                            wordBreak: "break-all",
                                        }, children: file.name })) : null] }), !hasEvidence ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary" }, children: t("claimEvidenceRequired", "Enter a payment reference or attach a screenshot to continue.") })) : null, fileError ? (_jsx(Alert, { severity: "error", sx: { borderRadius: 2 }, children: fileError })) : null, mutation.isError ? (_jsx(Alert, { severity: "error", sx: { borderRadius: 2 }, children: extractApiError(mutation.error, "We could not record that payment.") })) : null] })] }), _jsxs(DialogActions, { sx: DIALOG_ACTIONS_SX, children: [_jsx(Button, { onClick: onClose, disabled: mutation.isPending, sx: { borderRadius: 2 }, children: t("dismiss", "Cancel") }), _jsx(Button, { variant: "contained", sx: { borderRadius: 2 }, disabled: mutation.isPending ||
                            Boolean(fileError) ||
                            methodId === "" ||
                            !hasEvidence, onClick: () => mutation.mutate({
                            appointmentId: appointment.id,
                            paymentMethodId: methodId,
                            paymentReference: reference.trim() || null,
                            amount,
                            currency: appointment.currency ?? null,
                            file,
                        }), children: mutation.isPending ? (_jsx(CircularProgress, { size: 18, color: "inherit" })) : (t("claimSubmit", "Submit")) })] })] }));
}
